// Canonical L1 `intento` labels, for the FILTER only. The stored values are
// never changed:
//   PRENOTAZIONE → Richiesta prenotazione, SEGNALAZIONE → Segnalazione,
//   ROUTING → Richiesta trasferimento, INFORMAZIONE → Richiesta informazioni,
//   everything else (Generico, stray/empty values) → Non classificabile.
// What lives here is the outbound half: turning a canonical pick back into the
// concrete raw values (or the catch-all token) before the request leaves, and
// listing which labels are available to pick. The inbound half — folding a
// breakdown for display — moved to the backend, so that the chart, the export
// and the too-brief rule are decided in one place instead of three.

const RAW_TO_CANON: Record<string, string> = {
  PRENOTAZIONE: 'Richiesta prenotazione',
  PRENOTAZIONI: 'Richiesta prenotazione',
  SEGNALAZIONE: 'Segnalazione',
  SEGNALAZIONI: 'Segnalazione',
  ROUTING: 'Richiesta trasferimento',
  TRASFERIMENTO: 'Richiesta trasferimento',
  SMISTAMENTO: 'Richiesta trasferimento',
  INFORMAZIONE: 'Richiesta informazioni',
  INFORMAZIONI: 'Richiesta informazioni',
};

// ONE word for the residue across L1, L2 and L3 — see call_analytics.NON_CLASSIFICABILE.
// L1 used to say "Altro" and L2/L3 "Non classificato": two near-identical labels
// on neighbouring charts, which a reader assumes must mean different things.
export const NON_CLASSIFICABILE = 'Non classificabile';

// Bucket of the brief, not-transferred calls (call_analytics.BREVI_LABEL). It is
// a bar on every chart but never a filter value: those calls carry no reason,
// office or service, and the dedicated brevi report selects them.
export const BREVI_LABEL = 'Chiamate brevi non trasferite';

// The tokens stay distinct: they are wire values for two different dimensions,
// and the server expands each into its own condition.
export const NON_CLASSIFICATO_TOKEN = '__NON_CLASSIFICATO__';

// Map a picked L2/L3 value to what the backend filters on.
export function expandCategoriaFilter(values: string[]): string[] {
  return values.map((v) => (v === NON_CLASSIFICABILE ? NON_CLASSIFICATO_TOKEN : v));
}

// "Richiesta informazioni" arrives split by the event-search flag; each half is
// selected by its own token (call_analytics.INFO_EVENTI_TOKEN / INFO_SERVIZI_TOKEN),
// since no raw value alone picks it.
const INFO_TOKENS: Record<string, string> = {
  'Richiesta informazioni eventi': '__INFO_EVENTI__',
  'Richiesta informazioni servizi': '__INFO_SERVIZI__',
};

export const INTENTO_ORDER = [
  'Richiesta prenotazione', 'Segnalazione', 'Richiesta trasferimento',
  ...Object.keys(INFO_TOKENS), NON_CLASSIFICABILE,
];

// Token the backend expands to "every value not in the named set, incl. NULL/
// empty" — kept in sync with call_analytics.INTENTO_ALTRO_TOKEN.
export const ALTRO_TOKEN = '__ALTRO__';

// canonical label → its raw values (static inverse of RAW_TO_CANON).
const CANON_TO_RAWS: Record<string, string[]> = {};
for (const [raw, canon] of Object.entries(RAW_TO_CANON)) {
  (CANON_TO_RAWS[canon] ??= []).push(raw);
}

export function canonIntento(raw: string): string {
  return RAW_TO_CANON[(raw ?? '').trim().toUpperCase()] ?? NON_CLASSIFICABILE;
}

// Expand canonical intento picks to what the backend filters on: raw values for
// the named categories, and the catch-all token for "Altro".
export function expandIntentoFilter(intento: string[]): string[] {
  // A saved report may still name the unsplit 'Richiesta informazioni': it keeps
  // matching both halves through CANON_TO_RAWS.
  return intento.flatMap((c) => (c === NON_CLASSIFICABILE ? [ALTRO_TOKEN]
    : INFO_TOKENS[c] ? [INFO_TOKENS[c]] : CANON_TO_RAWS[c] ?? [c]));
}

// Canonical option labels present in the data, in a stable order.
// The breakdown now arrives already folded to canonical labels, so these are
// compared as-is. Anything outside INTENTO_ORDER is dropped by construction —
// which is what keeps the too-brief bucket out of the intent menu: it describes
// the call's duration, not what the caller wanted, so it is not selectable here.
export function canonIntentoOptions(values: string[]): string[] {
  const present = new Set(values);
  return INTENTO_ORDER.filter((l) => present.has(l));
}

